import React, { useEffect, useState } from 'react';
import {
  Table, Button, Space, Breadcrumb, Pagination, Flex, Popconfirm
} from 'antd';
import { HomeOutlined, CheckOutlined, EditOutlined} from '@ant-design/icons';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { apiRequest } from '../../../../service/apiRequest';
import { showError, showSuccess } from '../../../../components/errorHandler';
import { theme } from 'antd';

import LeadModal from './LeadModal';

interface Lead {
  lead_id: number;
  lead_name: string;
  lead_class: string;
  class_name: string;
  lead_remark: string;
  lead_current_status: string; // e.g., "pending", "approved"
  created_by: string;
}
interface Classes {
  class_id: number;
  class_name: string;
}

const LeadMaster: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [classes, setClasses] = useState<Classes[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Lead | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchLeads = async () => {
    try {
      const data = await apiRequest<Lead[]>({ method: 'get', url: '/leads' });
      setLeads(data);
    } catch {
      showError('Failed to fetch leads');
    }
  };
  const handleEditClick = (record: Lead) => {
    setInitialValues(record);
    setIsModalVisible(true);
    setIsEditMode(true);
  };
  const handleApprove = async (id: number) => {
    try {
      await apiRequest({ method: 'put', url: `/update-status-lead/${id}` });
      showSuccess('Lead approved');
      fetchLeads();
    } catch {
      showError('Failed to approve lead');
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'lead_name',
      key: 'lead_name',
    },
    {
      title: 'Mobile',
      dataIndex: 'lead_mobile',
      key: 'lead_mobile',
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
    },
    {
      title: 'Status',
      dataIndex: 'lead_current_status',
      key: 'lead_current_status',
      render: (status: string) => (
        <span style={{ color: status === 'Approve' ? 'green' : 'orange' }}>{status}</span>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Lead) => (
        <Space>
          {record.lead_current_status !== 'Approve' ? (
            <>
              {currentModule?.right_edit === 'Y' && (
                <Button onClick={() => handleEditClick(record)}>
                  <EditOutlined />
                </Button>
              )}
              {currentModule?.right_edit === 'Y' && (
                <Popconfirm
                  title="Are you sure you want to approve this lead?"
                  onConfirm={() => handleApprove(record.lead_id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    Approve
                  </Button>
                </Popconfirm>
              )}
            </>
          ) : (
            <span>Approved</span>
          )}
        </Space>

      ),
    },
  ];

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Lead Master</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button
            type="primary"
            onClick={() => {
              setInitialValues(null);
              setIsModalVisible(true);
            }}
          >
            Add Lead
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Lead>
          tableLayout="auto"
          columns={columns}
          rowKey="lead_id"
          dataSource={leads.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No leads found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, leads.length)} of{' '}
                {leads.length}
              </span>
              <Pagination
                {...pagination}
                total={leads.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />

        <LeadModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onCreate={fetchLeads}
          // initialValues={initialValues}
          initialValues={
            initialValues
              ? {
                ...initialValues,
                lead_class: String(initialValues.lead_class), // ✅ Cast to string
              }
              : null
          }

          isEditMode={isEditMode}
        />
      </div>
    </>
  );
};

export default LeadMaster;
